import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { BrandProvider } from "@/brand/BrandProvider";
import {
  UserCog, RotateCcw, LayoutDashboard, Building2, BedDouble, Users, CalendarRange, CalendarDays, Tag, BarChart3, Briefcase, ClipboardList, Wallet, Sparkles, FileText, MessageCircle, Landmark, BookOpen, ListTree, HandCoins, Receipt, PieChart, ClipboardCheck, AlertOctagon, ScrollText, Truck, FileSignature, Link2, Bot,
} from "lucide-react";
import { api } from "@/lib/api";
import { Sidebar, MobileHeader, MobileTabs } from "@/components/Sidebar";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Dashboard from "@/pages/Dashboard";
import Pousadas from "@/pages/Pousadas";
import Quartos from "@/pages/Quartos";
import Hospedes from "@/pages/Hospedes";
import Reservas from "@/pages/Reservas";
import Calendario from "@/pages/Calendario";
import Tarifas from "@/pages/Tarifas";
import Relatorios from "@/pages/Relatorios";
import Empresas from "@/pages/Empresas";
import Checkins from "@/pages/Checkins";
import ContasFinanceiras from "@/pages/ContasFinanceiras";
import MovimentosFinanceiros from "@/pages/MovimentosFinanceiros";
import PlanoContas from "@/pages/PlanoContas";
import ContasReceber from "@/pages/ContasReceber";
import ContasPagar from "@/pages/ContasPagar";
import DashboardFinanceiro from "@/pages/DashboardFinanceiro";
import ConciliacaoBancaria from "@/pages/ConciliacaoBancaria";
import ConferenciaReservas from "@/pages/ConferenciaReservas";
import DivergenciasFinanceiras from "@/pages/DivergenciasFinanceiras";
import ExtratoConsolidado from "@/pages/ExtratoConsolidado";
import Fornecedores from "@/pages/Fornecedores";
import DocumentosFiscais from "@/pages/DocumentosFiscais";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PublicCheckin from "@/pages/PublicCheckin";
import PublicCheckinEmpresa from "@/pages/PublicCheckinEmpresa";
import PousadaOcupacao from "@/pages/PousadaOcupacao";
import CleaningPanel from "@/pages/CleaningPanel";
import CleaningQuarto from "@/pages/CleaningQuarto";
import AdminLimpeza from "@/pages/AdminLimpeza";
import UsuariosLimpeza from "@/pages/UsuariosLimpeza";
import Orcamentos from "@/pages/Orcamentos";
import WhatsAppCentral from "@/pages/WhatsAppCentral";
import AgenteIA from "@/pages/AgenteIA";
import UsuariosPermissoes from "@/pages/UsuariosPermissoes";
import ResetFinanceiro from "@/pages/ResetFinanceiro";

// F-MOTOR-RESERVAS.3.0 — site comercial público.
import { PublicShell } from "@/components/public/PublicShell";
import Home from "@/pages/public/Home";
import PousadasList from "@/pages/public/PousadasList";
import PousadaDetail from "@/pages/public/PousadaDetail";
import QuartoDetail from "@/pages/public/QuartoDetail";
import Reservar from "@/pages/public/Reservar";
import Contato from "@/pages/public/Contato";
import NotFound from "@/pages/public/NotFound";

// Navegação do painel administrativo. Todos os `to` prefixados com `/admin`.
const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true, mobile: true },
  { to: "/admin/pousadas", label: "Pousadas", icon: Building2, testid: "nav-pousadas", mobile: true },
  { to: "/admin/quartos", label: "Quartos", icon: BedDouble, testid: "nav-quartos", mobile: true },
  { to: "/admin/tarifas", label: "Tarifas", icon: Tag, testid: "nav-tarifas", mobile: false },
  { to: "/admin/empresas", label: "Empresas", icon: Briefcase, testid: "nav-empresas", mobile: false },
  { to: "/admin/hospedes", label: "Hóspedes", icon: Users, testid: "nav-hospedes", mobile: true },
  { to: "/admin/reservas", label: "Reservas", icon: CalendarRange, testid: "nav-reservas", mobile: true },
  { to: "/admin/orcamentos", label: "Orçamentos", icon: FileText, testid: "nav-orcamentos", mobile: true },
  { to: "/admin/calendario", label: "Calendário", icon: CalendarDays, testid: "nav-calendario", mobile: false },
  { to: "/admin/checkins", label: "Check-ins", icon: ClipboardList, testid: "nav-checkins", mobile: false },
  // F13.15 — item "Financeiro" (legado) removido da navegação. Dashboard Financeiro F13.6 é o ponto oficial.
  { to: "/admin/financeiro/dashboard", label: "Financeiro", icon: Wallet, testid: "nav-financeiro", mobile: false },
  { to: "/admin/financeiro/contas", label: "Bancos e Contas", icon: Landmark, testid: "nav-contas-financeiras", mobile: false },
  { to: "/admin/financeiro/movimentos", label: "Livro-Caixa", icon: BookOpen, testid: "nav-movimentos-financeiros", mobile: false },
  { to: "/admin/financeiro/plano-contas", label: "Plano de Contas", icon: ListTree, testid: "nav-plano-contas", mobile: false },
  { to: "/admin/financeiro/contas-receber", label: "Contas a Receber", icon: HandCoins, testid: "nav-contas-receber", mobile: false },
  { to: "/admin/financeiro/contas-pagar", label: "Contas a Pagar", icon: Receipt, testid: "nav-contas-pagar", mobile: false },
  { to: "/admin/financeiro/fornecedores", label: "Fornecedores", icon: Truck, testid: "nav-fornecedores", mobile: false },
  { to: "/admin/financeiro/documentos-fiscais", label: "Documentos Fiscais", icon: FileSignature, testid: "nav-documentos-fiscais", mobile: false },
  { to: "/admin/financeiro/conciliacao", label: "Conciliação Bancária", icon: Link2, testid: "nav-conciliacao-bancaria", mobile: false },
  { to: "/admin/financeiro/conferencia", label: "Conferência de Reservas", icon: ClipboardCheck, testid: "nav-conferencia-reservas", mobile: false },
  { to: "/admin/financeiro/divergencias", label: "Divergências", icon: AlertOctagon, testid: "nav-divergencias", mobile: false },
  { to: "/admin/financeiro/extrato", label: "Extrato Consolidado", icon: ScrollText, testid: "nav-extrato", mobile: false },
  { to: "/admin/whatsapp/central", label: "Central WhatsApp", icon: MessageCircle, testid: "nav-whatsapp-central", mobile: false },
  { to: "/admin/ia/agente", label: "Agente IA", icon: Bot, testid: "nav-agente-ia", mobile: true },
  { to: "/admin/limpeza", label: "Limpeza", icon: Sparkles, testid: "nav-admin-limpeza", mobile: false },
  { to: "/admin/relatorios", label: "Relatórios", icon: BarChart3, testid: "nav-relatorios", mobile: false },
  { to: "/admin/usuarios", label: "Usuários e Permissões", icon: UserCog, testid: "nav-usuarios-permissoes", mobile: true },
  { to: "/admin/reset-financeiro", label: "Reset Financeiro", icon: RotateCcw, testid: "nav-reset-financeiro", mobile: false },
];

const painelLiberado = (u) => !!(u && (u.is_admin || u.is_finance || u.is_accountant));
// Perfis por unidade: só a gestão de usuários (quando permitida) até o isolamento por unidade.
const estadoAuth = (u) => {
  if (painelLiberado(u)) return "ok";
  if (u?.pode_gerenciar_usuarios) return "unidade";
  if (u?.is_cleaning) return "cleaning-only";
  if (u?.perfil_unidade) return "pendente";
  return "forbidden";
};

function AdminShell() {
  const [authState, setAuthState] = useState(null); // null|"ok"|"no"|"forbidden"|"cleaning-only"
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      const u = location.state.user;
      setUser(u);
      setAuthState(estadoAuth(u));
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setAuthState(estadoAuth(data));
      } catch { setAuthState("no"); }
    })();
  }, [location.state]);

  // Perfil Contador: aplica classe global para ocultar botões de escrita via CSS.
  useEffect(() => {
    const root = document.documentElement;
    const readOnly = !!(user && user.is_accountant && !user.is_admin && !user.is_finance);
    root.classList.toggle("perfil-contador", readOnly);
    return () => root.classList.remove("perfil-contador");
  }, [user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("logout error", err); }
    window.location.href = "/login";
  };

  if (authState === null) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500">Carregando…</div>;
  }
  if (authState === "no") return <Navigate to="/login" replace />;
  if (authState === "cleaning-only") return <Navigate to="/limpeza" replace state={{ user }} />;
  if (authState === "forbidden" || authState === "pendente") {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center" data-testid={`auth-${authState}`}>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">Acesso restrito</div>
          <h1 className="font-serif text-2xl text-stone-900 mb-3">
            {authState === "pendente" ? "Acesso em preparação" : "Esta conta não é autorizada"}
          </h1>
          <p className="text-stone-600 text-sm mb-6">
            {authState === "pendente"
              ? "Seu cadastro está ativo, mas os módulos por unidade serão liberados após a etapa de isolamento por unidade."
              : "Somente contas autorizadas pelo dono podem acessar o painel."}
            {user?.email && <><br /><span className="text-xs">Logado como {user.email}</span></>}
          </p>
          <button onClick={logout} data-testid="btn-forbidden-logout"
            className="text-sm text-[#083A6B] underline">
            Sair e entrar com outra conta
          </button>
        </div>
      </div>
    );
  }

  if (authState === "unidade" && location.pathname !== "/admin/usuarios") {
    return <Navigate to="/admin/usuarios" replace />;
  }
  if (authState === "ok" && user?.is_finance && !user?.is_admin && !user?.is_accountant && location.pathname === "/admin") {
    // F13.15 — redireciona para o Dashboard Financeiro oficial (F13.6).
    return <Navigate to="/admin/financeiro/dashboard" replace />;
  }

  // Contador (read-only): navega em tudo, exceto rotas de gestão de usuários/limpeza.
  const isContador = user?.is_accountant && !user?.is_admin && !user?.is_finance;
  // Contador: sem admin/limpeza e sem Central WhatsApp (bloqueada no backend).
  const contadorNav = nav.filter((n) => !["/admin/limpeza", "/admin/whatsapp/central", "/admin/ia/agente", "/admin/usuarios", "/admin/reset-financeiro"].includes(n.to));
  // Financeiro (sem admin/contador): Financeiro + Central WhatsApp
  const financeNav = nav.filter((n) => [
    "/admin/financeiro/dashboard", "/admin/financeiro/contas", "/admin/financeiro/movimentos",
    "/admin/financeiro/plano-contas", "/admin/financeiro/contas-receber", "/admin/financeiro/contas-pagar",
    "/admin/financeiro/fornecedores", "/admin/financeiro/documentos-fiscais", "/admin/financeiro/conciliacao",
    "/admin/financeiro/conferencia", "/admin/financeiro/divergencias", "/admin/financeiro/extrato",
    "/admin/whatsapp/central",
  ].includes(n.to));
  const unidadeNav = nav.filter((n) => n.to === "/admin/usuarios");
  const visibleNav = authState === "unidade" ? unidadeNav
    : user?.is_admin ? nav : (isContador ? contadorNav : financeNav);

  return (
    <div className="App flex bg-[#F5F1E8]">
      <Sidebar user={user} onLogout={logout} navItems={visibleNav} />
      <div className="flex-1 flex flex-col min-w-0">
        <MobileHeader onLogout={logout} />
        <main className="flex-1 p-6 lg:p-12 pb-24 lg:pb-12 fade-up">
          <Outlet />
        </main>
      </div>
      <MobileTabs navItems={visibleNav} />
    </div>
  );
}

/**
 * Shell restrito para colaboradoras da limpeza.
 */
function CleaningRouteShell() {
  const [state, setState] = useState(null); // null|"ok"|"no"|"forbidden"
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      const u = location.state.user;
      setUser(u);
      if (u.is_cleaning || u.is_admin) setState("ok");
      else setState("forbidden");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        if (data.is_cleaning || data.is_admin) setState("ok");
        else setState("forbidden");
      } catch { setState("no"); }
    })();
  }, [location.state]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("logout error", err); }
    window.location.href = "/login";
  };

  if (state === null) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500">Carregando…</div>;
  }
  if (state === "no") return <Navigate to="/login" replace />;
  if (state === "forbidden") {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">Acesso restrito</div>
          <h1 className="font-serif text-2xl text-stone-900 mb-3">Conta não autorizada para a Limpeza</h1>
          <p className="text-stone-600 text-sm mb-6">
            Peça ao administrador para cadastrar seu e-mail em Colaboradoras de Limpeza.
          </p>
          <button onClick={logout} className="text-sm text-[#083A6B] underline">Sair</button>
        </div>
      </div>
    );
  }

  return <Outlet context={{ user, onLogout: logout }} />;
}

function CleaningPanelRoute() {
  const { user, onLogout } = useOutletContext();
  return <CleaningPanel user={user} onLogout={onLogout} />;
}
function CleaningQuartoRoute() {
  const { user, onLogout } = useOutletContext();
  return <CleaningQuarto user={user} onLogout={onLogout} />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Autenticação / Fluxos independentes */}
      <Route path="/login" element={<Login />} />
      <Route path="/checkin" element={<PublicCheckin />} />
      <Route path="/checkin/empresa" element={<PublicCheckinEmpresa />} />

      {/* Limpeza — fluxo próprio (não migrado para /admin). */}
      <Route element={<CleaningRouteShell />}>
        <Route path="/limpeza" element={<CleaningPanelRoute />} />
        <Route path="/limpeza/quartos/:id" element={<CleaningQuartoRoute />} />
      </Route>

      {/* F-MOTOR-RESERVAS.3.0 — Site comercial público. */}
      <Route element={<PublicShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/pousadas" element={<PousadasList />} />
        <Route path="/pousadas/:slug" element={<PousadaDetail />} />
        <Route path="/quartos/:id" element={<QuartoDetail />} />
        <Route path="/reservar/:quartoId" element={<Reservar />} />
        <Route path="/contato" element={<Contato />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Painel administrativo — todas as rotas sob /admin/*. */}
      <Route element={<AdminShell />}>
        <Route path="/admin" element={<Dashboard />} />
        <Route path="/admin/pousadas" element={<Pousadas />} />
        <Route path="/admin/pousadas/:id/ocupacao" element={<PousadaOcupacao />} />
        <Route path="/admin/quartos" element={<Quartos />} />
        <Route path="/admin/tarifas" element={<Tarifas />} />
        <Route path="/admin/empresas" element={<Empresas />} />
        <Route path="/admin/hospedes" element={<Hospedes />} />
        <Route path="/admin/reservas" element={<Reservas />} />
        <Route path="/admin/orcamentos" element={<Orcamentos />} />
        <Route path="/admin/whatsapp/central" element={<WhatsAppCentral />} />
        <Route path="/admin/ia/agente" element={<AgenteIA />} />
        <Route path="/admin/calendario" element={<Calendario />} />
        <Route path="/admin/checkins" element={<Checkins />} />
        <Route path="/admin/financeiro" element={<Navigate to="/admin/financeiro/dashboard" replace />} />
        <Route path="/admin/financeiro/dashboard" element={<DashboardFinanceiro />} />
        <Route path="/admin/financeiro/contas" element={<ContasFinanceiras />} />
        <Route path="/admin/financeiro/movimentos" element={<MovimentosFinanceiros />} />
        <Route path="/admin/financeiro/plano-contas" element={<PlanoContas />} />
        <Route path="/admin/financeiro/contas-receber" element={<ContasReceber />} />
        <Route path="/admin/financeiro/contas-pagar" element={<ContasPagar />} />
        <Route path="/admin/financeiro/fornecedores" element={<Fornecedores />} />
        <Route path="/admin/financeiro/documentos-fiscais" element={<DocumentosFiscais />} />
        <Route path="/admin/financeiro/conciliacao" element={<ConciliacaoBancaria />} />
        <Route path="/admin/financeiro/conferencia" element={<ConferenciaReservas />} />
        <Route path="/admin/financeiro/divergencias" element={<DivergenciasFinanceiras />} />
        <Route path="/admin/financeiro/extrato" element={<ExtratoConsolidado />} />
        <Route path="/admin/relatorios" element={<Relatorios />} />
        <Route path="/admin/limpeza" element={<AdminLimpeza />} />
        <Route path="/admin/usuarios-limpeza" element={<UsuariosLimpeza />} />
        <Route path="/admin/usuarios" element={<UsuariosPermissoes />} />
        <Route path="/admin/reset-financeiro" element={<ResetFinanceiro />} />
      </Route>

      {/* Redirects de compatibilidade dos caminhos admin legados (sem /admin). */}
      <Route path="/pousadas/:id/ocupacao" element={<Navigate to="/admin/pousadas" replace />} />
      <Route path="/quartos" element={<Navigate to="/admin/quartos" replace />} />
      <Route path="/tarifas" element={<Navigate to="/admin/tarifas" replace />} />
      <Route path="/empresas" element={<Navigate to="/admin/empresas" replace />} />
      <Route path="/hospedes" element={<Navigate to="/admin/hospedes" replace />} />
      <Route path="/reservas" element={<Navigate to="/admin/reservas" replace />} />
      <Route path="/orcamentos" element={<Navigate to="/admin/orcamentos" replace />} />
      <Route path="/whatsapp/central" element={<Navigate to="/admin/whatsapp/central" replace />} />
      <Route path="/ia/agente" element={<Navigate to="/admin/ia/agente" replace />} />
      <Route path="/calendario" element={<Navigate to="/admin/calendario" replace />} />
      <Route path="/checkins" element={<Navigate to="/admin/checkins" replace />} />
      <Route path="/financeiro" element={<Navigate to="/admin/financeiro/dashboard" replace />} />
      <Route path="/financeiro/*" element={<FinanceiroLegacyRedirect />} />
      <Route path="/relatorios" element={<Navigate to="/admin/relatorios" replace />} />
      <Route path="/usuarios-limpeza" element={<Navigate to="/admin/usuarios-limpeza" replace />} />
    </Routes>
  );
}

// Reencaminha rotas /financeiro/* legadas para /admin/financeiro/*.
function FinanceiroLegacyRedirect() {
  const location = useLocation();
  const target = location.pathname.replace(/^\/financeiro/, "/admin/financeiro");
  return <Navigate to={target + location.search} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrandProvider>
        <HelmetProvider>
          <BrowserRouter>
            <Toaster richColors position="top-right" />
            <AppRouter />
          </BrowserRouter>
        </HelmetProvider>
      </BrandProvider>
    </ErrorBoundary>
  );
}
